import React from 'react';
import { Clock, Calendar, FileText, BarChart2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useContentPlanStore } from '../../../stores/contentPlanStore';
import { useCalendarStore } from '../../../stores/calendarStore';
import { useAnalyticsStore } from '../../../stores/analyticsStore';

export default function RecentActivity() {
  const navigate = useNavigate();
  const { plans } = useContentPlanStore();
  const { events } = useCalendarStore();
  const { entries } = useAnalyticsStore();
  
  // Get recent activities from different sources
  const getRecentActivities = () => {
    const recentPlans = plans.slice(0, 2).map(plan => ({
      type: 'plan',
      id: plan.id,
      title: plan.title,
      date: new Date(plan.createdAt),
      path: '/dashboard/planos'
    }));
    
    const recentEvents = events.slice(0, 2).map(event => ({
      type: 'event',
      id: event.id,
      title: event.title,
      date: new Date(event.start),
      path: '/dashboard/calendario'
    }));
    
    const recentAnalytics = entries.slice(0, 2).map(entry => ({
      type: 'analytics',
      id: entry.id,
      title: entry.contentName,
      date: new Date(entry.date),
      path: '/dashboard/analises'
    }));
    
    return [...recentPlans, ...recentEvents, ...recentAnalytics]
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 5);
  };
  
  const activities = getRecentActivities();
  
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'plan':
        return <FileText className="h-5 w-5 text-purple-600" />;
      case 'event':
        return <Calendar className="h-5 w-5 text-blue-600" />;
      case 'analytics':
        return <BarChart2 className="h-5 w-5 text-green-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };
  
  const formatDate = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);
    
    if (diffMins < 60) {
      return `${diffMins} min atrás`;
    } else if (diffHours < 24) {
      return `${diffHours} h atrás`;
    } else if (diffDays < 7) {
      return `${diffDays} dias atrás`;
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };
  
  if (activities.length === 0) {
    return null;
  }
  
  return (
    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Clock className="h-5 w-5 mr-2 text-gray-500" />
        Atividade Recente
      </h3>
      
      <div className="space-y-4">
        {activities.map((activity) => (
          <div 
            key={`${activity.type}-${activity.id}`}
            onClick={() => navigate(activity.path)}
            className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center mr-4 shadow-sm">
              {getActivityIcon(activity.type)}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{activity.title}</p>
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="h-3.5 w-3.5 mr-1" />
                {formatDate(activity.date)}
              </div>
            </div>
            <div className="ml-4">
              <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-xs capitalize">
                {activity.type === 'plan' ? 'Plano' : 
                 activity.type === 'event' ? 'Evento' : 'Análise'}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-center">
        <button 
          onClick={() => navigate('/dashboard/planos')}
          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
        >
          Ver todas as atividades
        </button>
      </div>
    </div>
  );
}